<template>
  <div
    class="mypage-switch-to-paid-plan-pane"
    :style="{ 'padding-bottom': `${state.actionButtonsHeight + 22}px` }"
  >
    <comparison-plan-section
      :showTrialNotice="isEligibleTrialPlan()"
      :isTrialAds="isTrialAds"
      :sectionTitle="$tc('SignupPage.SelectPlanPage.comparison.comparisonDescriptionForFree')"
    />
    <action-buttons-section
      class="mypage-switch-to-paid-plan-pane__action-buttons"
      :scrollTargetSelector="'.mypage-switch-to-paid-plan-pane'"
      :visible="true"
      ref="actionButtons"
    >
      <ul class="mypage-switch-to-paid-plan-pane__action-list">
        <li
          v-for="(subscription, index) in subscriptionItems"
          :key="`subscription${index}`"
          class="mypage-switch-to-paid-plan-pane__action-item"
        >
          <mypage-select-plan-button-parts
            :name="subscription.name"
            :price="subscription.price"
            :priceForButton="subscription.priceForButton"
            :unit="subscription.unit"
            :bargainPriced="subscription.bargainPriced"
            :annualPlanPriceDividedBy12="subscription.annualPlanPriceDividedBy12"
            :isEligibleTrialPlan="isEligibleTrialPlan()"
            @onClick="purchase(subscription)"
          />
        </li>
      </ul>
      <p
        v-if="isEligibleTrialPlan()"
        class="mypage-switch-to-paid-plan-pane__action-note"
        v-html="$tc('MypagePage.MypageSwitchToPaidPlanPage.forTrial.note')"
      />
      <atom-button
        class="mypage-switch-to-paid-plan-pane__action-restoration"
        :linkText="$tc('MypagePage.MypageSwitchToPaidPlanPage.restoration')"
        :colorType="'tertiary'"
        @onClick="restorePurchase()"
      />
      <atom-button
        v-if="isTrialAds"
        class="mypage-switch-to-paid-plan-pane__action-close"
        :linkText="$tc('common.close')"
        colorType="secondary"
        @onClick="moveToTop()"
      />
      <fieldset-checkbox-parts
        v-if="isTrialAds"
        class="mypage-switch-to-paid-plan-pane__action-do-not-show"
        :value="inputData.doNotShowNextTime"
        :label="$tc('MypagePage.MypageSwitchToPaidPlanPage.forTrial.FormInput.doNotShowNextTime')"
        @changed="toggleDoNotShowNextTime"
      />
    </action-buttons-section>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  inject,
  onMounted,
  reactive,
  ref,
  Ref,
} from '@vue/composition-api'
import VueRouter from 'vue-router'
import { PluginApi } from 'vue-loading-overlay'
import ComparisonPlanSection from '@/components/SignupPage/common/ComparisonPlanSection.vue'
import ActionButtonsSection from '@/components/common/form/ActionButtonsSection.vue'
import MypageSelectPlanButtonParts from '@/components/MypageSwitchToPaidPlanPage/MypageSwitchToPaidPlanPane/parts/MypageSelectPlanButtonParts.vue'
import AtomButton from '@/components/atoms/AtomButton.vue'
import DeviceInfo from '@/util/DeviceInfo'
import useInAppPurchase from '@/components/hook/inAppPurchase/useInAppPurchase'
import I18n from '@/locales/I18n'
import { SubscriptionPlanType } from '@/store/stores/collectionModule/documents/GeneralTypes'
import StoreUtil from '@/store/StoreUtil'
import Logger from '@/util/logger/Logger'
import MessageDialogStore from '@/store/stores/pageStore/common/MessageDialogStore'
import Const from '@/util/Const'
import ContractInfoStore from '@/store/stores/pageStore/common/ContractInfoStore'
import FieldsetCheckboxParts from '@/components/common/form/FieldsetCheckboxParts.vue'
import IndexedDBAccessor from '@/store/stores/IndexedDBstore/IndexedDBAccessor'

/**
 * 定期購入商品情報の型
 */
type SubscriptionItemType = {
  type: SubscriptionPlanType
  id: string
  name: string
  price: string
  priceForButton: string
  unit: string
  bargainPriced?: string
  owned: boolean
  // 12で割った年額プランの価格
  annualPlanPriceDividedBy12?: string
}

/**
 * 入力項目
 */
type InputDataType = {
  doNotShowNextTime: boolean
}

/**
 * マイページ: アプリ内課金 有料プラン購入ペイン
 */
export default defineComponent({
  name: 'MypageSwitchToPaidPlanPane',
  components: {
    FieldsetCheckboxParts,
    AtomButton,
    MypageSelectPlanButtonParts,
    ActionButtonsSection,
    ComparisonPlanSection,
  },
  props: {
    /**
     * トライアル広告ページかどうか
     */
    isTrialAds: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const mypagePageStore = StoreUtil.useStore('MypagePageStore')
    const { fetchContractInfo, fetchInAppPurchaseHistory, inAppPurchaseHistory, ownContractInfo } =
      mypagePageStore
    const {
      store,
      sfgoSubscriptions,
      purchaseSubscription,
      restore,
      setDisplaySubscriptionData,
      eligibleTrialPlan,
    } = useInAppPurchase()
    const { setDoNotShowTrialAdsNextTime } = IndexedDBAccessor()

    const router = inject('router') as VueRouter
    const loading = inject('loading') as PluginApi

    const actionButtons = ref<InstanceType<typeof ActionButtonsSection> | null>(null)
    const state = reactive({
      actionButtonsHeight: 383,
    })

    /** 入力データ */
    const inputData = ref<InputDataType>({
      doNotShowNextTime: false,
    })

    /** 購入したプランタイプ */
    const purchasedPlanType: Ref<SubscriptionPlanType | undefined> = ref(undefined)

    /**
     * アプリ内課金 BEに連携するサブスクリプション購入時のUUID
     */
    const purchaseSubscriptionUuid = computed(() => {
      // ストアの変更が検知されなかったため、Loggerを定義することによって変更を検知できるように対応した
      Logger.info(
        `MypageSwitchToPaidPlanPane#purchaseSubscriptionUuid: ${mypagePageStore.purchaseSubscriptionUuid.value}`,
      )
      return mypagePageStore.purchaseSubscriptionUuid.value
    })

    /**
     * 製品IDを取得
     */
    const getProductId = (cycle: SubscriptionPlanType) => sfgoSubscriptions.value[cycle]?.id ?? ''

    /**
     * 定価を取得
     */
    const getListPrice = (cycle: SubscriptionPlanType) =>
      sfgoSubscriptions.value[cycle]?.listPrice ?? ''

    /**
     * 定価（数値）を取得
     */
    const getListPriceNum = (cycle: SubscriptionPlanType) =>
      sfgoSubscriptions.value[cycle]?.listPriceMicros ?? 0

    /**
     * 購入状態を取得
     */
    const getOwned = (cycle: SubscriptionPlanType) => sfgoSubscriptions.value[cycle]?.owned ?? false

    /**
     * 月額プランと比べて、年額プランを契約した場合のお得額を計算
     */
    const bargainPriced = computed(() => {
      const annualPlanPriceNum = getListPriceNum('annual')
      const monthlyPlanPriceNum = getListPriceNum('monthly')

      if (!annualPlanPriceNum || !monthlyPlanPriceNum) {
        return ''
      }

      // お得額
      const moneySaved = `${(
        100 -
        (annualPlanPriceNum / (monthlyPlanPriceNum * 12)) * 100
      ).toFixed()}%`
      return I18n.t('MypagePage.MypageSwitchToPaidPlanPage.plans.annual.bargainPriced', {
        percent: moneySaved,
      }).toString()
    })

    /**
     * 12で割った年額プランの価格
     */
    const annualPlanPriceDividedBy12 = computed(() => {
      const annualPlanPrice = getListPrice('annual')

      if (!annualPlanPrice) {
        return ''
      }

      // 通貨記号を抽出（先頭の非数値文字をすべて）
      const currencySymbol = annualPlanPrice.match(/^[^\d]+/)?.[0] ?? ''

      // 数値に変換
      const numericPrice = parseInt(annualPlanPrice.replace(/[^\d]/g, ''), 10)

      // 12で割った料金
      const monthlyPrice = Math.round(numericPrice / 12).toLocaleString()

      return `${currencySymbol}${monthlyPrice}`
    })

    /**
     * トライアルプラン適用可能かどうか
     */
    const isEligibleTrialPlan = () => eligibleTrialPlan(ownContractInfo.value.inAppPurchase)

    /**
     * 定期購入商品情報
     */
    const subscriptionItems: Ref<SubscriptionItemType[]> = computed(() => {
      const trialButtonLabel = I18n.tc('common.registration')

      // トライアルプラン適用可能かどうか
      const applicableTrialPlan = isEligibleTrialPlan()

      return [
        {
          type: 'annual',
          id: getProductId('annual'),
          name: I18n.tc('MypagePage.MypageSwitchToPaidPlanPage.plans.annual.title'),
          price: getListPrice('annual'),
          priceForButton: applicableTrialPlan ? trialButtonLabel : getListPrice('annual'),
          unit: applicableTrialPlan
            ? ''
            : I18n.tc('MypagePage.MypageSwitchToPaidPlanPage.plans.annual.unit'),
          bargainPriced: bargainPriced.value,
          owned: getOwned('annual'),
          annualPlanPriceDividedBy12: annualPlanPriceDividedBy12.value,
        },
        {
          type: 'monthly',
          id: getProductId('monthly'),
          name: I18n.tc('MypagePage.MypageSwitchToPaidPlanPage.plans.monthly.title'),
          price: getListPrice('monthly'),
          priceForButton: applicableTrialPlan ? trialButtonLabel : getListPrice('monthly'),
          unit: applicableTrialPlan
            ? ''
            : I18n.tc('MypagePage.MypageSwitchToPaidPlanPage.plans.monthly.unit'),
          owned: getOwned('monthly'),
        },
      ]
    })

    /**
     * purchaseSubscriptionUuidを取得
     * useInAppPurchaseのfinishPurchaseでストアにトランザクションIDを保存しているが、
     * ストアに保存されているトランザクションIDが即時取得できないため、setIntervalを使ってトランザクションIDを取得する処理にしている
     */
    const getPurchaseSubscriptionUuid = async () => {
      let count = 0
      return new Promise<string>((resolve) => {
        const timerId = window.setInterval(async () => {
          if (purchaseSubscriptionUuid.value) {
            // purchaseSubscriptionUuidをセットできたらsetIntervalを停止
            clearInterval(timerId)
            resolve(purchaseSubscriptionUuid.value)
            return
          }

          count += 1
          if (count >= 10) {
            // 10回試してもpurchaseSubscriptionUuidを取得できない場合、setIntervalを停止
            clearInterval(timerId)
            resolve(purchaseSubscriptionUuid.value)
          }
        }, 500)
      })
    }

    /**
     * アプリ内課金履歴情報を定期的に取得する。
     * @param latestSubscriptionUuid 最新のトランザクションから取得したpurchaseId（Google Play課金時のみ値が渡ってくる）
     */
    const pollingInAppPurchaseHistory = async (latestSubscriptionUuid = '') => {
      // ストアに保存されているトランザクションIDが即時取得できないため、以下の処理でトランザクションIDを取得できるようにする
      await getPurchaseSubscriptionUuid()

      let count = 0
      return new Promise<void>((resolve) => {
        const inAppPurchaseId = purchaseSubscriptionUuid.value
          ? purchaseSubscriptionUuid.value
          : latestSubscriptionUuid
        const timerId = window.setInterval(async () => {
          await fetchInAppPurchaseHistory(inAppPurchaseId).then(() => {
            Logger.debug(
              `useInAppPurchase#pollingInAppPurchaseHistory: Polling to fetch inAppPurchaseHistory.`,
            )
          })

          count += 1
          if (count === 30 || inAppPurchaseHistory.value?.status === 'Complete') {
            // 30回実行したら（90秒経ったら）または契約情報の更新処理が完了したらポーリングを停止する
            clearInterval(timerId)
            resolve()
          }
        }, Const.CONTRACT_INFO_POLING_INTERVAL)
      })
    }

    /**
     * アプリ内課金履歴情報を定期的に取得する処理を実行
     * @param latestSubscriptionUuid 最新のトランザクションから取得したpurchaseId（Google Play課金時のみ値が渡ってくる）
     */
    const execPollingInAppPurchaseHistory = async (latestSubscriptionUuid = '') => {
      // アプリ内課金履歴情報を定期的に取得する
      await pollingInAppPurchaseHistory(latestSubscriptionUuid)
      if (inAppPurchaseHistory.value?.status !== 'Complete') {
        MessageDialogStore.value.open({
          title: I18n.tc(
            'MypagePage.MypageSwitchToPaidPlanPage.errors.getInAppPurchaseHistoryError.title',
          ),
          message: I18n.tc(
            'MypagePage.MypageSwitchToPaidPlanPage.errors.getInAppPurchaseHistoryError.message',
          ),
        })
        return { isSuccess: false }
      }

      return { isSuccess: true }
    }

    /**
     * purchaseTokenを取得する
     * purchaseTokenを取得できない場合はリトライ処理を行い、最大10回までリトライする
     */
    const retryToGetPurchaseToken = async () => {
      let purchaseToken = ''
      let attempts = 0
      const maxAttempts = 10

      while (!purchaseToken && attempts < maxAttempts) {
        // 購入ステータスを最新にする
        // eslint-disable-next-line no-await-in-loop
        await setDisplaySubscriptionData()
        const ownedSubscription = Object.values(sfgoSubscriptions.value).find((v) => v?.owned)
        if (ownedSubscription) {
          const latestReceipt = store.findInLocalReceipts(ownedSubscription)
          purchaseToken = latestReceipt?.purchaseId ?? ''
        }

        if (!purchaseToken) {
          // 2秒待機してから再試行
          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve) => {
            setTimeout(resolve, 2000)
          })
        }
        attempts += 1
      }

      return purchaseToken
    }

    /**
     * 商品を購入する
     */
    const purchase = async (subscription: SubscriptionItemType) => {
      if (mypagePageStore.isPurchasingInAppPurchase.value) {
        // アプリ内課金でプラン購入中は購入ボタンを押せない
        return
      }

      const contractInfoLoader = loading.show()
      const contractInfoResults = await ContractInfoStore.value.fetchUserContractInfo()
      contractInfoLoader.hide()
      const contractInfoFailedResult = contractInfoResults.find((result) => !result?.isSuccess)
      if (contractInfoFailedResult) {
        await MessageDialogStore.value.open({
          title: I18n.tc(
            'MypagePage.MypageSwitchToPaidPlanPage.errors.fetchContractInfoError.title',
          ),
          message: I18n.tc(
            'MypagePage.MypageSwitchToPaidPlanPage.errors.fetchContractInfoError.message',
          ),
          errorApiResponse: contractInfoFailedResult.response,
        })
        // 契約情報取得に失敗した場合は、強制ログアウトする
        await router.replace({ path: '/logout' })
        return
      }

      if (!ContractInfoStore.value.isFreePlan) {
        // 無料プラン以外は課金できないようにする
        await MessageDialogStore.value.open({
          title: I18n.tc('MypagePage.MypageSwitchToPaidPlanPage.errors.alreadyPaidMember.title'),
          message: I18n.tc(
            'MypagePage.MypageSwitchToPaidPlanPage.errors.alreadyPaidMember.message',
          ),
        })
        // ログアウト
        router.replace({ path: '/logout' })
        return
      }

      // iOSの場合、決済失敗後に決済情報を更新せずそのまま自動キャンセルとなった場合、ownedがtureのまま返ってきてしまう。
      // そのため、iOSの場合は購入処理まで実施できるようにし、同じサブスクリプションを二重課金している場合はiOS標準のエラーモーダルを出す挙動にしている
      if (DeviceInfo.isAndroid() && subscriptionItems.value.some((v) => v.owned)) {
        let title = ''
        let message = ''
        if (DeviceInfo.isiOS()) {
          title = I18n.tc(
            'MypagePage.MypageSwitchToPaidPlanPage.errors.iOS.alreadyPurchasedPlan.title',
          )
          message = I18n.t(
            'MypagePage.MypageSwitchToPaidPlanPage.errors.iOS.alreadyPurchasedPlan.message',
            {
              url: Const.EXTERNAL_LINKS.IN_APP_PURCHASE.SUBSCRIPTION_MANAGEMENT.IOS,
            },
          ).toString()
        } else if (DeviceInfo.isAndroid()) {
          title = I18n.tc(
            'MypagePage.MypageSwitchToPaidPlanPage.errors.android.alreadyPurchasedPlan.title',
          )
          message = I18n.t(
            'MypagePage.MypageSwitchToPaidPlanPage.errors.android.alreadyPurchasedPlan.message',
            {
              url: Const.EXTERNAL_LINKS.IN_APP_PURCHASE.SUBSCRIPTION_MANAGEMENT.ANDROID,
            },
          ).toString()
        }

        // 既に有料プランを購入済みの場合は、購入処理を実施しない
        MessageDialogStore.value.open({
          title,
          message,
        })
        return
      }

      // BEに連携するサブスクリプション購入時のUUIDをクリア
      mypagePageStore.purchaseSubscriptionUuid.value = ''
      // 年額プラン、月額プランどちらを購入したか判別できるようにする
      purchasedPlanType.value = subscription.type

      // アプリ内のボタンを押せないようにする
      mypagePageStore.isPurchasingInAppPurchase.value = true
      // 対象商品を購入する
      const purchaseResult = await purchaseSubscription(subscription.id)

      if (!purchaseResult?.isSuccess) {
        // アプリ内のボタンを押せるようにする
        mypagePageStore.isPurchasingInAppPurchase.value = false

        // 購入処理をキャンセル、または失敗した場合は後続処理を実行しない
        return
      }

      const results: Array<{ isSuccess: boolean }> = []
      let pollingResult: { isSuccess: boolean } | null = null
      let subscriptionUuid = ''

      // アプリ内課金時に契約情報を更新する処理を実行
      const loader = loading.show()

      if (DeviceInfo.isAndroid()) {
        // Androidの場合、ストアからpurchaseTokenを取得できないことがあるため、その時はリトライしてpurchaseTokenを取得する
        subscriptionUuid = await retryToGetPurchaseToken()
      }

      // アプリ内課金履歴情報を定期的に取得する処理を実行
      pollingResult = await execPollingInAppPurchaseHistory(subscriptionUuid)
      results.push(pollingResult)

      if (
        DeviceInfo.isAndroid() &&
        pollingResult?.isSuccess &&
        mypagePageStore.inAppPurchaseTransaction.value
      ) {
        // Androidの場合、購入処理が完了したらトランザクションを閉じる
        mypagePageStore.inAppPurchaseTransaction.value.finish()
      }

      loader.hide()
      // アプリ内のボタンを押せるようにする
      mypagePageStore.isPurchasingInAppPurchase.value = false

      const failedResult = results.find((result) => !result?.isSuccess)
      if (!failedResult) {
        // アプリ内課金での購入が完了して、契約情報が完了した場合、マイページに戻り完了モーダルを出す
        if (purchasedPlanType.value === 'annual') {
          router.push({ name: 'MypageTopPage', query: { modalId: 'completeAnnualPlan' } })
        } else if (purchasedPlanType.value === 'monthly') {
          router.push({ name: 'MypageTopPage', query: { modalId: 'competeMonthlyPlan' } })
        }
      } else {
        // マイページトップへ遷移
        router.replace({ name: 'MypageTopPage' })
      }
    }

    /**
     * 購入情報を復元する
     */
    const restorePurchase = async () => {
      if (mypagePageStore.isPurchasingInAppPurchase.value) {
        // アプリ内課金でプラン購入中は復元ボタンを押せない
        return
      }

      // BEに連携するサブスクリプション購入時のUUIDをクリア
      mypagePageStore.purchaseSubscriptionUuid.value = ''
      // 購入したプラン情報をクリア
      purchasedPlanType.value = undefined

      const loader = loading.show()
      await restore()
      await fetchContractInfo()
      loader.hide()

      // TODO: 契約プラン情報が更新できなかった場合エラーモーダルを表示する
      // @see https://pitchbase.atlassian.net/wiki/spaces/SL01/pages/3806625934/API
      const isSuccess = true
      // isSuccess = xxx

      if (isSuccess) {
        router.push({ name: 'MypageTopPage', query: { modalId: 'completeRestore' } })
      } else {
        // マイページトップへ遷移
        router.replace({ name: 'MypageTopPage' })
      }
    }

    /**
     * トップページに遷移する
     */
    const moveToTop = async () => {
      router.replace({ name: 'HomePage' })
    }

    /**
     * トップページに遷移する
     */
    const toggleDoNotShowNextTime = (value: boolean) => {
      setDoNotShowTrialAdsNextTime({
        displayed: value,
      })
      inputData.value.doNotShowNextTime = value
    }

    onMounted(async () => {
      state.actionButtonsHeight =
        actionButtons.value?.$el.getBoundingClientRect().height || state.actionButtonsHeight

      /**
       * 表示する商品情報データを設定
       */
      setDisplaySubscriptionData()

      await fetchContractInfo()
    })

    return {
      state,
      inputData,
      subscriptionItems,
      purchase,
      actionButtons,
      restorePurchase,
      isEligibleTrialPlan,
      moveToTop,
      toggleDoNotShowNextTime,
    }
  },
})
</script>

<style scoped lang="scss">
.mypage-switch-to-paid-plan-pane {
  width: 100%;
  max-width: $responsiveMypageMaxWidth;
  height: calc(
    100vh - #{$headerPaneHeight} - #{$subHeaderPaneHeight} - #{var(--safe-area-inset-bottom)}
  );
  padding-bottom: 286px;
  margin: 0 auto;
  overflow-y: auto;
  overflow-x: hidden;

  &__action {
    &-list {
      width: 100%;
    }

    &-buttons {
      bottom: 0;

      ::v-deep {
        .action-buttons-section__inner {
          display: block;
          max-width: 480px;
          padding: 16px;
          margin: 0 auto;
        }
      }
    }

    &-item {
      + .mypage-switch-to-paid-plan-pane__action-item {
        margin-top: 8px;
      }
    }

    &-note {
      margin-top: 8px;
      @include fontSetTextMedium(11);

      text-align: center;
      color: $black70;
    }

    &-restoration {
      margin-top: 8px;
    }

    &-close {
      margin-top: 8px;
    }

    &-do-not-show {
      margin-top: 8px;
      display: flex;
      justify-content: center;
    }
  }
}
</style>
